/**
 * Programme data migration utilities.
 * Handles automatic migration of old programme exports to current schema version.
 * @module utils/migrate-programme
 */

import type { Programme } from "@/types";

import { normalizeDeliveryModality } from "./helpers";
import { normaliseLegacyVocabulary } from "./legacy-vocabulary";
import { teachingPeriodLabelFrom } from "./teaching-period";

/** Current programme data schema version. */
export const SCHEMA_VERSION = 18;

/**
 * Loose structural shape of an award standard as consumed by the standards
 * helper functions. Accepts both the strict {@link import("@/types").AwardStandard}
 * type and the partial hierarchical objects produced in tests/legacy data.
 */
interface StandardData {
  id: string;
  name?: string;
  nfqLevels?: {
    level: number;
    indicatorGroups?: {
      id?: string;
      name: string;
      indicators?: {
        id: string;
        name: string;
        descriptor: string;
        descriptorText?: string;
      }[];
    }[];
  }[];
}

/** A single issue raised while validating PLO → standard mappings. */
interface StandardMappingIssue {
  ploId: string;
  ploIndex: number;
  mappingIndex: number;
  message: string;
}

/**
 * Migrates programme data from schema v1 to v2.
 * Converts singular award standard fields to arrays and normalizes delivery modality.
 */
function migrateV1toV2(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 2,
  };

  // Migration: convert old single award standard to array format
  if (typeof migrated.awardStandardId === "string") {
    const oldId = migrated.awardStandardId ?? "";
    const oldName = migrated.awardStandardName ?? "";
    migrated.awardStandardIds = oldId ? [oldId] : [];
    migrated.awardStandardNames = oldName ? [oldName] : [];
    delete migrated.awardStandardId;
    delete migrated.awardStandardName;
  }

  // Ensure arrays exist
  if (!Array.isArray(migrated.awardStandardIds)) {
    migrated.awardStandardIds = [];
  }
  if (!Array.isArray(migrated.awardStandardNames)) {
    migrated.awardStandardNames = [];
  }

  // Ensure versions array exists
  if (!Array.isArray(migrated.versions)) {
    migrated.versions = [];
  }

  // Migration: convert old deliveryModalities array to single deliveryModality in versions
  (migrated.versions as Record<string, unknown>[]).forEach((draft) => {
    if (Array.isArray(draft.deliveryModalities) && !draft.deliveryModality) {
      draft.deliveryModality = (draft.deliveryModalities as unknown[])[0] || "F2F";
      delete draft.deliveryModalities;
    }
  });

  // Clean up legacy programme-level delivery fields
  delete migrated.deliveryMode;
  delete migrated.syncPattern;
  delete migrated.deliveryModalities;

  // Clean up any deprecated fields that might have been stored
  if (migrated.standardsCache) {
    delete migrated.standardsCache;
  }

  if (migrated._cachedStandards) {
    delete migrated._cachedStandards;
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v2 to v3.
 * Updates standard IDs, thread names, and criteria casing to match new standards format.
 */
function migrateV2toV3(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 3,
  };

  // Old standard IDs → new standard IDs mapping
  const standardIdMap: Record<string, string> = {
    "qqi-computing-l6-9": "computing",
    "qqi-professional-awards-l5-9": "professional",
    "qqi-generic-major-masters-l9": "generic-masters",
  };

  // Thread name mappings: old prefixed names → new simple names
  const threadNameMap: Record<string, string> = {
    "Competence-Context": "Context",
    "Competence-Role": "Role",
    "Competence-Learning to Learn": "Learning to Learn",
    "Competence-Insight": "Insight",
    "Know-how & Skill-Range": "Range",
    "Know-how & Skill-Selectivity": "Selectivity",
    "Knowledge-Breadth": "Breadth",
    "Knowledge-Kind": "Kind",
  };

  // Criteria case normalization
  const criteriaNameMap: Record<string, string> = {
    "Know-how & Skill": "Know-How & Skill",
  };

  // Migrate programme-level award standard IDs
  if (Array.isArray(migrated.awardStandardIds)) {
    migrated.awardStandardIds = (migrated.awardStandardIds as string[]).map(
      (id) => standardIdMap[id] || id,
    );
  }

  // Get default standard ID for mappings missing one (use first migrated ID)
  const defaultStandardId = (migrated.awardStandardIds as string[] | undefined)?.[0] ?? null;

  // Migrate PLO standard mappings
  if (Array.isArray(migrated.plos)) {
    migrated.plos = (migrated.plos as Record<string, unknown>[]).map((plo) => {
      if (!plo.standardMappings || !Array.isArray(plo.standardMappings)) {
        return plo;
      }

      const migratedMappings = (plo.standardMappings as Record<string, unknown>[]).map(
        (mapping) => {
          const newMapping: Record<string, unknown> = { ...mapping };

          // Migrate old standard ID to new
          if (newMapping.standardId && standardIdMap[newMapping.standardId as string]) {
            newMapping.standardId = standardIdMap[newMapping.standardId as string];
          }

          // Add missing standardId
          if (!newMapping.standardId && defaultStandardId) {
            newMapping.standardId = defaultStandardId;
          }

          // Fix criteria case
          if (newMapping.criteria && criteriaNameMap[newMapping.criteria as string]) {
            newMapping.criteria = criteriaNameMap[newMapping.criteria as string];
          }

          // Migrate old thread names
          if (newMapping.thread && threadNameMap[newMapping.thread as string]) {
            newMapping.thread = threadNameMap[newMapping.thread as string];
          }

          return newMapping;
        },
      );

      return { ...plo, standardMappings: migratedMappings };
    });
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Validates that PLO standard mappings reference valid criteria and threads.
 */
export function validateStandardMappings(
  programme: Record<string, unknown>,
  standards: StandardData[],
): { errors: StandardMappingIssue[]; warnings: StandardMappingIssue[]; isValid: boolean } {
  const errors: StandardMappingIssue[] = [];
  const warnings: StandardMappingIssue[] = [];

  if (!programme.plos || !Array.isArray(programme.plos)) {
    return { errors: [], warnings: [], isValid: true };
  }

  (programme.plos as Record<string, unknown>[]).forEach((plo, ploIdx) => {
    if (!plo.standardMappings || !Array.isArray(plo.standardMappings)) {
      return;
    }

    const ploId = plo.id as string;

    (plo.standardMappings as Record<string, unknown>[]).forEach((mapping, mappingIdx) => {
      const criteria = mapping.criteria as string;
      const thread = mapping.thread as string;
      const standardId = mapping.standardId as string;

      // Find the standard
      const standard = standards.find((s) => s.id === standardId);
      if (!standard) {
        errors.push({
          ploId,
          ploIndex: ploIdx,
          mappingIndex: mappingIdx,
          message: `Standard '${standardId}' not found`,
        });
        return;
      }

      // Find the NFQ level
      const nfqLevel = programme.nfqLevel as number;
      if (!nfqLevel) {
        warnings.push({
          ploId,
          ploIndex: ploIdx,
          mappingIndex: mappingIdx,
          message: `Programme NFQ level not set, cannot validate mapping`,
        });
        return;
      }

      const levelData = standard.nfqLevels?.find((l) => l.level === nfqLevel);
      if (!levelData) {
        warnings.push({
          ploId,
          ploIndex: ploIdx,
          mappingIndex: mappingIdx,
          message: `Standard '${standardId}' has no data for NFQ level ${nfqLevel}`,
        });
        return;
      }

      // Find the indicator group (criteria)
      const group = levelData.indicatorGroups?.find((g) => g.name === criteria);
      if (!group) {
        errors.push({
          ploId,
          ploIndex: ploIdx,
          mappingIndex: mappingIdx,
          message: `Criteria '${criteria}' not found in standard '${standardId}' at NFQ level ${nfqLevel}`,
        });
        return;
      }

      // Find the indicator (thread)
      const indicator = group.indicators?.find((i) => i.name === thread);
      if (!indicator) {
        errors.push({
          ploId,
          ploIndex: ploIdx,
          mappingIndex: mappingIdx,
          message: `Thread '${thread}' not found under criteria '${criteria}' in standard '${standardId}'`,
        });
      }
    });
  });

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
  };
}

/**
 * Extracts standard indicators as a flat array for a specific NFQ level.
 * Provides backward compatibility with code expecting the old standards structure.
 */
export function getStandardIndicators(
  standard: StandardData | null | undefined,
  nfqLevel: number,
): {
  criteria: string;
  thread: string;
  descriptor: string;
  descriptorText: string;
  id: string;
  awardStandardId: string;
}[] {
  if (!standard) {
    return [];
  }

  const levelData = standard.nfqLevels?.find((l) => l.level === Number(nfqLevel));
  if (!levelData) {
    return [];
  }

  return levelData.indicatorGroups!.flatMap((group) =>
    group.indicators!.map((indicator) => ({
      criteria: group.name,
      thread: indicator.name,
      descriptor: indicator.descriptor,
      descriptorText: indicator.descriptorText!,
      id: indicator.id,
      awardStandardId: standard.id,
    })),
  );
}

/**
 * Returns the list of criteria names for a standard at a specific NFQ level.
 */
export function getCriteriaList(
  standard: StandardData | null | undefined,
  nfqLevel: number,
): string[] {
  if (!standard) {
    return [];
  }

  const levelData = standard.nfqLevels?.find((l) => l.level === Number(nfqLevel));
  if (!levelData) {
    return [];
  }

  return levelData.indicatorGroups!.map((g) => g.name);
}

/**
 * Returns the list of thread names for a specific criteria at an NFQ level.
 */
export function getThreadList(
  standard: StandardData | null | undefined,
  nfqLevel: number,
  criteria: string,
): string[] {
  if (!standard || !criteria) {
    return [];
  }

  const levelData = standard.nfqLevels?.find((l) => l.level === Number(nfqLevel));
  if (!levelData) {
    return [];
  }

  const group = levelData.indicatorGroups!.find((g) => g.name === criteria);
  if (!group) {
    return [];
  }

  return group.indicators!.map((i) => i.name);
}

/**
 * Returns the descriptor text for a specific criteria/thread combination.
 */
export function getDescriptor(
  standard: StandardData | null | undefined,
  nfqLevel: number,
  criteria: string,
  thread: string,
): string {
  if (!standard || !criteria || !thread) {
    return "";
  }

  const levelData = standard.nfqLevels?.find((l) => l.level === Number(nfqLevel));
  if (!levelData) {
    return "";
  }

  const group = levelData.indicatorGroups!.find((g) => g.name === criteria);
  if (!group) {
    return "";
  }

  const indicator = group.indicators!.find((i) => i.name === thread);
  return indicator?.descriptor || "";
}

/**
 * Migrates programme data from schema v3 to v4.
 * Converts ploToModules (PLO → Module mapping) to ploToMimlos (PLO → MIMLO mapping).
 * When a module was mapped to a PLO, all MIMLOs of that module become mapped.
 */
function migrateV3toV4(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 4,
  };

  // Convert ploToModules to ploToMimlos
  if (migrated.ploToModules && typeof migrated.ploToModules === "object") {
    const ploToMimlos: Record<string, string[]> = {};
    const modules = (migrated.modules ?? []) as Record<string, unknown>[];

    Object.entries(migrated.ploToModules as Record<string, unknown>).forEach(
      ([ploId, moduleIds]) => {
        const mimloIds: string[] = [];

        ((moduleIds as unknown[]) ?? []).forEach((moduleId) => {
          const mod = modules.find((m) => m.id === moduleId);
          if (mod && Array.isArray(mod.mimlos)) {
            (mod.mimlos as Record<string, unknown>[]).forEach((mimlo) => {
              if (mimlo.id && !mimloIds.includes(mimlo.id as string)) {
                mimloIds.push(mimlo.id as string);
              }
            });
          }
        });

        if (mimloIds.length > 0) {
          ploToMimlos[ploId] = mimloIds;
        }
      },
    );

    migrated.ploToMimlos = ploToMimlos;
    delete migrated.ploToModules;
  }

  // Initialize ploToMimlos if not present
  if (!migrated.ploToMimlos) {
    migrated.ploToMimlos = {};
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v4 to v5.
 * Adds teachingWeeks property to all programme versions.
 */
function migrateV4toV5(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 5,
  };

  // Add teachingWeeks to all versions
  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((draft) => {
      draft.teachingWeeks ??= 12;
    });
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v5 to v6.
 *
 * v6 captures the institution-neutral sprint contract changes:
 * - renames the legacy institution-specific "school" field to the portable
 *   "discipline" field;
 * - backfills `moduleIds` on every elective group, reconstructing membership
 *   from any module whose `electiveGroupId` points at the group. Older data
 *   could omit `moduleIds` (modules linked to groups via `electiveGroupId`
 *   only), which downstream credit/exclusion logic assumed was always present;
 *   and
 * - ensures the institution-neutral `providerSettings` object and the core
 *   `modules` / `electiveDefinitions` arrays are present, so every entry point
 *   (store load, JSON import, reports library) yields structurally complete
 *   data without relying on a caller pre-seeding defaults.
 */
function migrateV5toV6(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 6,
  };

  // Institution-neutral terminology: rename legacy "school" → "discipline".
  if ("school" in migrated) {
    migrated.discipline ??= migrated.school ?? "";
    delete migrated.school;
  }

  // Backfill elective group moduleIds. Reconstruct membership from modules that
  // reference the group via electiveGroupId; default to an empty array.
  if (Array.isArray(migrated.electiveDefinitions)) {
    const modules: Record<string, unknown>[] = Array.isArray(migrated.modules)
      ? (migrated.modules as Record<string, unknown>[])
      : [];
    (migrated.electiveDefinitions as Record<string, unknown>[]).forEach((def) => {
      const groups: Record<string, unknown>[] = Array.isArray(def?.groups)
        ? (def.groups as Record<string, unknown>[])
        : [];
      groups.forEach((draft) => {
        if (!Array.isArray(draft.moduleIds)) {
          draft.moduleIds = modules.filter((m) => m.electiveGroupId === draft.id).map((m) => m.id);
        }
      });
    });
  }

  // Ensure required structure is present. Defined locally (not imported from the
  // store) to avoid a circular dependency: store.ts imports this module.
  if (!migrated.providerSettings || typeof migrated.providerSettings !== "object") {
    migrated.providerSettings = { name: "", abbreviation: "", contactEmail: "", website: "" };
  }
  if (!Array.isArray(migrated.modules)) {
    migrated.modules = [];
  }
  if (!Array.isArray(migrated.electiveDefinitions)) {
    migrated.electiveDefinitions = [];
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v6 to v7.
 *
 * v7 makes teaching weeks a per-stage value. For every version's stages it
 * backfills `teachingWeeks` from the owning version's `teachingWeeks` (falling
 * back to 12), preserving any stage that already carries its own value.
 */
function migrateV6toV7(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 7,
  };

  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((version) => {
      const fallback = (version.teachingWeeks as number | undefined) ?? 12;
      if (Array.isArray(version.stages)) {
        (version.stages as Record<string, unknown>[]).forEach((draft) => {
          draft.teachingWeeks ??= fallback;
        });
      }
    });
  }

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v7 to v8.
 *
 * v8 extends the institution-neutral `providerSettings` shape with additional
 * managed tenant/provider fields (all optional). No existing field changes: the
 * step simply stamps the new schema version and refreshes `updatedAt`, preserving
 * every existing field — including the `providerSettings` object and its `name` —
 * so persisted data and JSON round-trips stay lossless.
 */
function migrateV7toV8(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 8,
  };

  migrated.updatedAt = new Date().toISOString();

  return migrated;
}

/**
 * Migrates programme data from schema v8 to v9.
 *
 * v9 introduces explicit HE descriptor content, canonical delivery-modality
 * keys, and separate academic-stage / teaching-period values.
 */
/**
 * Backfill a stage's teaching period and academic stage from its name.
 *
 * Extracted from the v8 to v9 step rather than left inline: the version and
 * the stage are both working copies, so both would have to be named `draft`,
 * and nesting one inside the other would shadow. Pulling it out gives each
 * one its own scope.
 *
 * @param draft - The stage being migrated, mutated in place.
 * @param allTeachingPeriods - Whether every stage in the version is named for
 *   a semester or term, which is what makes an unnumbered stage stage 1.
 */
function migrateV8toV9Stage(draft: Record<string, unknown>, allTeachingPeriods: boolean): void {
  const name = String(draft.name ?? "").trim();
  if (draft.teachingPeriod == null && /^(semester|term)\b/i.test(name)) {
    draft.teachingPeriod = name;
  }
  if (draft.academicStage == null) {
    const explicitStage = name.match(/^(?:stage|year)\s*(\d+)/i);
    if (explicitStage) {
      draft.academicStage = Number(explicitStage[1]);
    } else if (allTeachingPeriods) {
      draft.academicStage = 1;
    }
  }
}

function migrateV8toV9(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 9,
  };

  const existingHeDescriptor =
    migrated.heDescriptor && typeof migrated.heDescriptor === "object"
      ? { ...(migrated.heDescriptor as Record<string, unknown>) }
      : {};
  if (
    existingHeDescriptor.rationale == null &&
    typeof migrated.rationale === "string" &&
    migrated.rationale.trim()
  ) {
    existingHeDescriptor.rationale = migrated.rationale;
  }
  migrated.heDescriptor = existingHeDescriptor;

  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((draft) => {
      const rawModality = typeof draft.deliveryModality === "string" ? draft.deliveryModality : "";
      const canonicalModality = normalizeDeliveryModality(rawModality);
      if (rawModality) {
        draft.deliveryModality = canonicalModality;
      }

      if (draft.deliveryPatterns && typeof draft.deliveryPatterns === "object") {
        const canonicalPatterns: Record<string, unknown> = {};
        Object.entries(draft.deliveryPatterns as Record<string, unknown>).forEach(
          ([key, value]) => {
            canonicalPatterns[normalizeDeliveryModality(key)] = value;
          },
        );
        draft.deliveryPatterns = canonicalPatterns;
      }

      if (!Array.isArray(draft.stages)) {
        return;
      }
      const stages = draft.stages as Record<string, unknown>[];
      const allTeachingPeriods = stages.every((stage) =>
        /^(semester|term)\b/i.test(String(stage.name ?? "").trim()),
      );
      stages.forEach((stage) => {
        migrateV8toV9Stage(stage, allTeachingPeriods);
      });
    });
  }

  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

function migrateLegacyAwardLinks(
  data: Record<string, unknown>,
  targetSchemaVersion: number,
): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: targetSchemaVersion,
  };
  const embeddedAwards: Record<string, unknown>[] = Array.isArray(migrated.embeddedAwards)
    ? (migrated.embeddedAwards as Record<string, unknown>[]).map((award) => ({ ...award }))
    : [];
  const usedIds = new Set(
    embeddedAwards.map((award) => (typeof award.id === "string" ? award.id : "")).filter(Boolean),
  );
  let awardSequence = 1;
  const nextAwardId = (): string => {
    let id = `embedded_award_${awardSequence}`;
    while (usedIds.has(id)) {
      awardSequence += 1;
      id = `embedded_award_${awardSequence}`;
    }
    usedIds.add(id);
    awardSequence += 1;
    return id;
  };
  const addUnique = (values: unknown, value: string): string[] => {
    const items = Array.isArray(values)
      ? values.filter((item): item is string => typeof item === "string")
      : [];
    return value && !items.includes(value) ? [...items, value] : items;
  };

  const legacyEmbedded =
    migrated.embeddedProgramme && typeof migrated.embeddedProgramme === "object"
      ? (migrated.embeddedProgramme as Record<string, unknown>)
      : null;
  if (legacyEmbedded) {
    const title = String(legacyEmbedded.title ?? "").trim();
    const hasLegacyContent =
      title ||
      String(legacyEmbedded.awardType ?? "").trim() ||
      Number(legacyEmbedded.totalCredits ?? 0) > 0 ||
      legacyEmbedded.nfqLevel != null;
    if (hasLegacyContent) {
      const existingLegacyAward = embeddedAwards.find(
        (award) =>
          award.kind !== "cas-component" &&
          String(award.title ?? "")
            .trim()
            .toLocaleLowerCase() === title.toLocaleLowerCase(),
      );
      if (!existingLegacyAward) {
        embeddedAwards.push({
          id: nextAwardId(),
          title,
          awardType: String(legacyEmbedded.awardType ?? ""),
          nfqLevel: legacyEmbedded.nfqLevel == null ? null : Number(legacyEmbedded.nfqLevel),
          credits: Number(legacyEmbedded.totalCredits ?? 0),
          kind: "embedded",
          stageIds: [],
          moduleIds: [],
          versionIds: [],
        });
      }
    }
  }

  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((version) => {
      if (!Array.isArray(version.stages)) {
        return;
      }
      (version.stages as Record<string, unknown>[]).forEach((stage) => {
        const exitAward =
          stage.exitAward && typeof stage.exitAward === "object"
            ? (stage.exitAward as Record<string, unknown>)
            : null;
        const title = String(exitAward?.awardTitle ?? "").trim();
        if (!exitAward?.enabled || !title) {
          return;
        }

        const linkedId =
          typeof exitAward.embeddedAwardId === "string" ? exitAward.embeddedAwardId : "";
        let award = embeddedAwards.find((candidate) => candidate.id === linkedId);
        award ??= embeddedAwards.find(
          (candidate) =>
            candidate.kind !== "cas-component" &&
            String(candidate.title ?? "")
              .trim()
              .toLocaleLowerCase() === title.toLocaleLowerCase(),
        );
        if (!award) {
          award = {
            id: nextAwardId(),
            title,
            awardType: "",
            nfqLevel: null,
            credits: 0,
            kind: "exit",
            stageIds: [],
            moduleIds: [],
            versionIds: [],
          };
          embeddedAwards.push(award);
        } else if (award.kind === "embedded") {
          award.kind = "embedded-and-exit";
        }

        const stageId = typeof stage.id === "string" ? stage.id : "";
        const versionId = typeof version.id === "string" ? version.id : "";
        award.stageIds = addUnique(award.stageIds, stageId);
        award.versionIds = addUnique(award.versionIds, versionId);
        exitAward.embeddedAwardId = award.id;
      });
    });
  }

  if (Array.isArray(migrated.modules)) {
    (migrated.modules as Record<string, unknown>[]).forEach((module) => {
      const componentCode = String(module.casComponentCode ?? "").trim();
      if (!componentCode) {
        return;
      }
      let award = embeddedAwards.find(
        (candidate) =>
          candidate.kind === "cas-component" &&
          String(candidate.casComponentCode ?? "").trim() === componentCode,
      );
      if (!award) {
        const levelMatch = componentCode.match(/^([1-6])/);
        award = {
          id: nextAwardId(),
          title: String(module.title ?? "").trim(),
          awardType: "FE Component Award",
          nfqLevel: levelMatch ? Number(levelMatch[1]) : null,
          credits: Number(module.credits ?? 0),
          kind: "cas-component",
          stageIds: [],
          moduleIds: [],
          versionIds: [],
          casComponentCode: componentCode,
        };
        embeddedAwards.push(award);
      }
      const moduleId = typeof module.id === "string" ? module.id : "";
      award.moduleIds = addUnique(award.moduleIds, moduleId);
    });
  }

  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((version) => {
      if (!Array.isArray(version.stages)) {
        return;
      }
      (version.stages as Record<string, unknown>[]).forEach((stage) => {
        const assignedModuleIds = new Set<string>();
        if (Array.isArray(stage.moduleIds)) {
          stage.moduleIds.forEach((moduleId) => {
            if (typeof moduleId === "string") {
              assignedModuleIds.add(moduleId);
            }
          });
        }
        if (Array.isArray(stage.modules)) {
          (stage.modules as Record<string, unknown>[]).forEach((assignment) => {
            if (typeof assignment.moduleId === "string") {
              assignedModuleIds.add(assignment.moduleId);
            }
          });
        }
        if (assignedModuleIds.size === 0) {
          return;
        }

        const stageId = typeof stage.id === "string" ? stage.id : "";
        const versionId = typeof version.id === "string" ? version.id : "";
        embeddedAwards.forEach((draft) => {
          const moduleIds = Array.isArray(draft.moduleIds)
            ? draft.moduleIds.filter((moduleId): moduleId is string => typeof moduleId === "string")
            : [];
          if (!moduleIds.some((moduleId) => assignedModuleIds.has(moduleId))) {
            return;
          }
          draft.stageIds = addUnique(draft.stageIds, stageId);
          draft.versionIds = addUnique(draft.versionIds, versionId);
        });
      });
    });
  }

  migrated.embeddedAwards = embeddedAwards;
  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/**
 * Migrates programme data from schema v9 to v10.
 *
 * v10 introduces first-class embedded awards. It preserves legacy fields while
 * converting programme-level embedded awards, stage exit awards, and CAS
 * component modules into a unified collection.
 */
function migrateV9toV10(data: Record<string, unknown>): Record<string, unknown> {
  return migrateLegacyAwardLinks(data, 10);
}

/**
 * Repairs v10 programmes authored before stage exit controls were linked to
 * first-class embedded awards.
 */
function migrateV10toV11(data: Record<string, unknown>): Record<string, unknown> {
  return migrateLegacyAwardLinks(data, 11);
}

/**
 * Adds explicit stage placement for elective choice slots.
 *
 * Earlier programmes placed elective modules into a stage separately. Any
 * assigned module from a definition identifies the stage containing that
 * choice. The migration records the slot and adds its remaining option modules
 * so downstream schedules retain the complete set of available choices.
 */
function migrateV11toV12(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 12,
  };
  const definitions = Array.isArray(migrated.electiveDefinitions)
    ? (migrated.electiveDefinitions as Record<string, unknown>[])
    : [];

  if (Array.isArray(migrated.versions)) {
    (migrated.versions as Record<string, unknown>[]).forEach((version) => {
      if (!Array.isArray(version.stages)) {
        return;
      }
      (version.stages as Record<string, unknown>[]).forEach((draft) => {
        const stageModules = Array.isArray(draft.modules)
          ? (draft.modules as Record<string, unknown>[])
          : [];
        const stageModuleIds = new Set(
          stageModules
            .map((entry) => (typeof entry.moduleId === "string" ? entry.moduleId : ""))
            .filter(Boolean),
        );
        const placements = Array.isArray(draft.electiveChoiceSlots)
          ? (draft.electiveChoiceSlots as Record<string, unknown>[]).map((slot) => ({
              ...slot,
            }))
          : [];
        definitions.forEach((definition) => {
          const definitionId = typeof definition.id === "string" ? definition.id : "";
          if (!definitionId) {
            return;
          }
          const groups = Array.isArray(definition.groups)
            ? (definition.groups as Record<string, unknown>[])
            : [];
          const optionModuleIds = Array.from(
            new Set(
              groups.flatMap((group) =>
                Array.isArray(group.moduleIds)
                  ? group.moduleIds.filter((id): id is string => typeof id === "string")
                  : [],
              ),
            ),
          );
          if (optionModuleIds.length === 0) {
            return;
          }

          let placement = placements.find((slot) => slot.electiveDefinitionId === definitionId);
          if (!placement && optionModuleIds.some((moduleId) => stageModuleIds.has(moduleId))) {
            const teachingPeriods = new Set(
              stageModules
                .filter((entry) => optionModuleIds.includes(String(entry.moduleId ?? "")))
                .map((entry) => (typeof entry.semester === "string" ? entry.semester : "")),
            );
            placement = {
              electiveDefinitionId: definitionId,
              semester: teachingPeriods.size === 1 ? Array.from(teachingPeriods)[0] : "",
            };
            placements.push(placement);
          }

          if (placement) {
            optionModuleIds.forEach((moduleId) => {
              if (!stageModuleIds.has(moduleId)) {
                stageModules.push({
                  moduleId,
                  semester: typeof placement?.semester === "string" ? placement.semester : "",
                });
                stageModuleIds.add(moduleId);
              }
            });
          }
        });

        draft.modules = stageModules;
        draft.electiveChoiceSlots = placements;
      });
    });
  }

  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/**
 * Adds an explicit outside-teaching-period flag to assessments.
 *
 * Earlier assessments are treated as occurring during teaching unless they
 * are subsequently marked otherwise.
 */
function migrateV12toV13(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 13,
  };

  if (Array.isArray(data.modules)) {
    migrated.modules = (data.modules as unknown[]).map((module) => {
      if (!module || typeof module !== "object") {
        return module;
      }

      const moduleRecord = module as Record<string, unknown>;
      if (!Array.isArray(moduleRecord.assessments)) {
        return { ...moduleRecord };
      }

      return {
        ...moduleRecord,
        assessments: (moduleRecord.assessments as unknown[]).map((assessment) => {
          if (!assessment || typeof assessment !== "object") {
            return assessment;
          }

          const assessmentRecord = assessment as Record<string, unknown>;
          return {
            ...assessmentRecord,
            outsideTeachingPeriod:
              typeof assessmentRecord.outsideTeachingPeriod === "boolean"
                ? assessmentRecord.outsideTeachingPeriod
                : false,
          };
        }),
      };
    });
  }

  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/** Introduces optional programme-version assessment overrides. */
function migrateV13toV14(data: Record<string, unknown>): Record<string, unknown> {
  return {
    ...data,
    schemaVersion: 14,
    updatedAt: new Date().toISOString(),
  };
}

/** Introduces explicit capstone, hurdle, and mandatory-pass assessment attributes. */
function migrateV14toV15(data: Record<string, unknown>): Record<string, unknown> {
  const addDefaults = (assessment: unknown): unknown => {
    if (!assessment || typeof assessment !== "object") {
      return assessment;
    }
    const record = assessment as Record<string, unknown>;
    return {
      ...record,
      isCapstone: typeof record.isCapstone === "boolean" ? record.isCapstone : false,
      isHurdle: typeof record.isHurdle === "boolean" ? record.isHurdle : false,
      mandatoryPass: typeof record.mandatoryPass === "boolean" ? record.mandatoryPass : false,
    };
  };

  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 15,
  };

  if (Array.isArray(data.modules)) {
    migrated.modules = (data.modules as unknown[]).map((module) => {
      if (!module || typeof module !== "object") {
        return module;
      }
      const moduleRecord = module as Record<string, unknown>;
      return {
        ...moduleRecord,
        assessments: Array.isArray(moduleRecord.assessments)
          ? (moduleRecord.assessments as unknown[]).map(addDefaults)
          : moduleRecord.assessments,
      };
    });
  }

  if (Array.isArray(data.versions)) {
    migrated.versions = (data.versions as unknown[]).map((version) => {
      if (!version || typeof version !== "object") {
        return version;
      }
      const versionRecord = version as Record<string, unknown>;
      if (
        !versionRecord.assessmentOverrides ||
        typeof versionRecord.assessmentOverrides !== "object" ||
        Array.isArray(versionRecord.assessmentOverrides)
      ) {
        return { ...versionRecord };
      }

      const overrides = Object.fromEntries(
        Object.entries(versionRecord.assessmentOverrides as Record<string, unknown>).map(
          ([moduleId, override]) => {
            if (!override || typeof override !== "object" || Array.isArray(override)) {
              return [moduleId, override];
            }
            const overrideRecord = override as Record<string, unknown>;
            return [
              moduleId,
              {
                ...overrideRecord,
                baseSharedAssessments: Array.isArray(overrideRecord.baseSharedAssessments)
                  ? (overrideRecord.baseSharedAssessments as unknown[]).map(addDefaults)
                  : overrideRecord.baseSharedAssessments,
                assessments: Array.isArray(overrideRecord.assessments)
                  ? (overrideRecord.assessments as unknown[]).map(addDefaults)
                  : overrideRecord.assessments,
              },
            ];
          },
        ),
      );

      return { ...versionRecord, assessmentOverrides: overrides };
    });
  }

  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/** Introduces structured participation for cross-module assessments. */
function migrateV15toV16(data: Record<string, unknown>): Record<string, unknown> {
  const addParticipation = (assessment: unknown): unknown => {
    if (!assessment || typeof assessment !== "object") {
      return assessment;
    }
    const record = assessment as Record<string, unknown>;
    return {
      ...record,
      participatingModules: Array.isArray(record.participatingModules)
        ? record.participatingModules
        : [],
    };
  };

  const migrated: Record<string, unknown> = {
    ...data,
    schemaVersion: 16,
  };

  if (Array.isArray(data.modules)) {
    migrated.modules = (data.modules as unknown[]).map((module) => {
      if (!module || typeof module !== "object") {
        return module;
      }
      const moduleRecord = module as Record<string, unknown>;
      return {
        ...moduleRecord,
        assessments: Array.isArray(moduleRecord.assessments)
          ? (moduleRecord.assessments as unknown[]).map(addParticipation)
          : moduleRecord.assessments,
      };
    });
  }

  if (Array.isArray(data.versions)) {
    migrated.versions = (data.versions as unknown[]).map((version) => {
      if (!version || typeof version !== "object") {
        return version;
      }
      const versionRecord = version as Record<string, unknown>;
      if (
        !versionRecord.assessmentOverrides ||
        typeof versionRecord.assessmentOverrides !== "object" ||
        Array.isArray(versionRecord.assessmentOverrides)
      ) {
        return { ...versionRecord };
      }

      const overrides = Object.fromEntries(
        Object.entries(versionRecord.assessmentOverrides as Record<string, unknown>).map(
          ([moduleId, override]) => {
            if (!override || typeof override !== "object" || Array.isArray(override)) {
              return [moduleId, override];
            }
            const overrideRecord = override as Record<string, unknown>;
            return [
              moduleId,
              {
                ...overrideRecord,
                baseSharedAssessments: Array.isArray(overrideRecord.baseSharedAssessments)
                  ? (overrideRecord.baseSharedAssessments as unknown[]).map(addParticipation)
                  : overrideRecord.baseSharedAssessments,
                assessments: Array.isArray(overrideRecord.assessments)
                  ? (overrideRecord.assessments as unknown[]).map(addParticipation)
                  : overrideRecord.assessments,
              },
            ];
          },
        ),
      );

      return { ...versionRecord, assessmentOverrides: overrides };
    });
  }

  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/**
 * Drops the stage-level exit award flag. v10 and v11 turned it into a
 * first-class award, which every reader now places at a stage.
 */
function migrateV16toV17(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = { ...data, schemaVersion: 17 };
  if (Array.isArray(data.versions)) {
    migrated.versions = (data.versions as unknown[]).map((version) => {
      if (!version || typeof version !== "object") {
        return version;
      }
      const versionRecord = version as Record<string, unknown>;
      if (!Array.isArray(versionRecord.stages)) {
        return versionRecord;
      }
      return {
        ...versionRecord,
        stages: (versionRecord.stages as unknown[]).map((stage) => {
          if (!stage || typeof stage !== "object") {
            return stage;
          }
          const { exitAward: _dropped, ...rest } = stage as Record<string, unknown>;
          return rest;
        }),
      };
    });
  }
  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/** A version's stages as records, whatever the document held. */
function stageRecords(version: unknown): Record<string, unknown>[] {
  const stages = (version as { stages?: unknown } | null)?.stages;
  return Array.isArray(stages)
    ? stages.filter(
        (stage): stage is Record<string, unknown> => !!stage && typeof stage === "object",
      )
    : [];
}

/**
 * A placement numbered across the version, "3" or "3, 4", renumbered within a
 * stage starting at `start`. Free text, and a number before the stage, stay.
 */
function stageLocalPlacement(value: unknown, start: number): unknown {
  if (typeof value !== "string" || !/^\d+(?:\s*,\s*\d+)*$/.test(value.trim())) {
    return value;
  }
  const local = value.split(",").map((part) => Number(part.trim()) - start + 1);
  return local.every((number) => number >= 1) ? local.join(", ") : value;
}

/** A stage's placements and elective slots renumbered from its first period. */
function renumberStagePlacements(stage: Record<string, unknown>, start: number) {
  const renumber = (entries: unknown) =>
    Array.isArray(entries)
      ? entries.map((entry: unknown) =>
          entry && typeof entry === "object" && "semester" in entry
            ? { ...entry, semester: stageLocalPlacement(entry.semester, start) }
            : entry,
        )
      : entries;
  return {
    ...stage,
    ...(Array.isArray(stage.modules) ? { modules: renumber(stage.modules) } : {}),
    ...(Array.isArray(stage.electiveChoiceSlots)
      ? { electiveChoiceSlots: renumber(stage.electiveChoiceSlots) }
      : {}),
  };
}

/** Each stage's first period numbered across the version in sequence order, as v17 stored them. */
function cumulativePeriodStarts(stages: Record<string, unknown>[]): Map<unknown, number> {
  const starts = new Map<unknown, number>();
  [...stages]
    .sort((a, b) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0))
    .reduce((next, stage) => {
      starts.set(stage, next);
      return next + Number(stage.semesterCount ?? 0);
    }, 1);
  return starts;
}

/**
 * The teaching period label moves from each stage to the programme, taking
 * the first one recorded, as migration 0061 does for stored programmes.
 * Placements stored numbered across the version are renumbered within their
 * stage, so Year 2's first period is 1 rather than 3. The version's teaching
 * weeks go; v7 copied them onto every stage, which is the only source now.
 */
function migrateV17toV18(data: Record<string, unknown>): Record<string, unknown> {
  const migrated: Record<string, unknown> = { ...data, schemaVersion: 18 };
  const versions = Array.isArray(data.versions) ? (data.versions as unknown[]) : [];
  const stageLabel = versions
    .flatMap(stageRecords)
    .map((stage) => teachingPeriodLabelFrom(stage.teachingPeriod))
    .find((label) => label !== undefined);
  if (typeof migrated.teachingPeriodLabel !== "string" && stageLabel !== undefined) {
    migrated.teachingPeriodLabel = stageLabel;
  }
  if (Array.isArray(data.versions)) {
    migrated.versions = versions.map((version) => {
      if (!version || typeof version !== "object") {
        return version;
      }
      const {
        teachingWeeks: _versionWeeks,
        durationWeeks: _durationWeeks,
        ...kept
      } = version as Record<string, unknown>;
      const stages = kept.stages;
      if (!Array.isArray(stages)) {
        return kept;
      }
      const starts = cumulativePeriodStarts(stageRecords(kept));
      return {
        ...kept,
        stages: stages.map((stage: unknown) => {
          if (!stage || typeof stage !== "object") {
            return stage;
          }
          const { teachingPeriod: _dropped, ...rest } = stage as Record<string, unknown>;
          return renumberStagePlacements(rest, starts.get(stage) ?? 1);
        }),
      };
    });
  }
  migrated.updatedAt = new Date().toISOString();
  return migrated;
}

/**
 * Migrates programme data from older schema versions to the current version.
 * Applies sequential migrations (v1→v2→v3→v4→v5→v6→v7→v8) as needed. The v5→v6
 * step renames legacy institution-specific fields (e.g. "school" → "discipline"),
 * backfills elective group moduleIds, and ensures provider settings and core
 * arrays are present. The v6→v7 step backfills each stage's teachingWeeks from
 * its owning version. The v7→v8 step stamps the extended (still
 * institution-neutral) provider settings shape, preserving every existing field.
 */

export function migrateProgramme(data: unknown): Programme {
  const record = data as Record<string, unknown>;
  const currentVersion = SCHEMA_VERSION;
  const dataVersion = (record.schemaVersion as number) || 1;

  // Already at the current version → return as-is (preserves reference
  // unless a retired vocabulary value needs mapping).
  if (dataVersion === currentVersion) {
    return normaliseLegacyVocabulary(data as Programme);
  }

  // Deep, not spread: the steps below reach into `versions[].stages[]` and
  // assign, so a shallow copy hands the caller's own objects to be rewritten.
  // Submission captured content that way and hashed a field the database
  // never held, which made approval report a content conflict.
  let migrated: Record<string, unknown> = structuredClone(record);

  // Apply migrations sequentially
  if (dataVersion < 2) {
    migrated = migrateV1toV2(migrated);
  }

  if (dataVersion < 3) {
    migrated = migrateV2toV3(migrated);
  }

  if (dataVersion < 4) {
    migrated = migrateV3toV4(migrated);
  }

  if (dataVersion < 5) {
    migrated = migrateV4toV5(migrated);
  }

  if (dataVersion < 6) {
    migrated = migrateV5toV6(migrated);
  }

  if (dataVersion < 7) {
    migrated = migrateV6toV7(migrated);
  }

  if (dataVersion < 8) {
    migrated = migrateV7toV8(migrated);
  }

  if (dataVersion < 9) {
    migrated = migrateV8toV9(migrated);
  }

  if (dataVersion < 10) {
    migrated = migrateV9toV10(migrated);
  }

  if (dataVersion < 11) {
    migrated = migrateV10toV11(migrated);
  }

  if (dataVersion < 12) {
    migrated = migrateV11toV12(migrated);
  }

  if (dataVersion < 13) {
    migrated = migrateV12toV13(migrated);
  }

  if (dataVersion < 14) {
    migrated = migrateV13toV14(migrated);
  }

  if (dataVersion < 15) {
    migrated = migrateV14toV15(migrated);
  }

  if (dataVersion < 16) {
    migrated = migrateV15toV16(migrated);
  }

  if (dataVersion < 17) {
    migrated = migrateV16toV17(migrated);
  }

  if (dataVersion < 18) {
    migrated = migrateV17toV18(migrated);
  }

  return normaliseLegacyVocabulary(migrated as Programme);
}
